/**
 * 🚀 Layout Utils 2025 - Helper functions for Revolutionary Layout Settings
 * 
 * Utility functions to apply layout settings in widgets with mobile/desktop responsiveness
 */

// Helper function to get responsive value
export const getResponsiveValue = (data, propertyName, isMobile = false, fallback = 'auto') => {
    const mobileProperty = `${propertyName}Mobile`;
    const desktopProperty = propertyName;
    
    if (isMobile && data[mobileProperty] !== undefined && data[mobileProperty] !== null && data[mobileProperty] !== '') {
        return data[mobileProperty];
    }
    
    if (data[desktopProperty] !== undefined && data[desktopProperty] !== null && data[desktopProperty] !== '') {
        return data[desktopProperty];
    }
    
    return fallback;
};

// Helper function to append px to numeric values
export const appendPx = (value) => {
    if (value === 'auto' || value === 'none' || value === 'inherit' || value === 'initial') {
        return value;
    }
    
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    
    const numValue = parseInt(value);
    if (isNaN(numValue)) {
        return 'auto';
    }
    
    return `${numValue}px`;
};

// Helper function to append % to numeric values
export const appendPercent = (value) => {
    if (value === 'auto' || value === 'none' || value === 'inherit' || value === 'initial') {
        return value;
    }
    
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    
    const numValue = parseInt(value);
    if (isNaN(numValue)) {
        return 'auto';
    }
    
    return `${numValue}%`;
};

// Generate complete responsive layout styles - nur Vertikale
export const generateLayoutStyles = (data, isMobile = false, additionalStyles = {}) => {
    const layoutStyles = {
        // Padding - nur vertikal
        paddingTop: appendPx(getResponsiveValue(data, 'paddingTop', isMobile, 0)),
        paddingBottom: appendPx(getResponsiveValue(data, 'paddingBottom', isMobile, 0)),
        
        // Margin - nur vertikal
        marginTop: appendPx(getResponsiveValue(data, 'marginTop', isMobile, 0)),
        marginBottom: appendPx(getResponsiveValue(data, 'marginBottom', isMobile, 0)),
        
        // Height Dimensions - nur Vertikale
        height: appendPx(getResponsiveValue(data, 'height', isMobile, 'auto')),
        
        // Min Height - immer anwenden wenn gesetzt
        ...(getResponsiveValue(data, 'minHeight', isMobile, 0) > 0 && {
            minHeight: appendPx(getResponsiveValue(data, 'minHeight', isMobile, 0))
        }),
        
        // Max Height - nur wenn aktiviert und gesetzt
        ...(data.maxHeight !== undefined && data.maxHeight !== null && data.maxHeight !== '' && data.maxHeight !== 'none' && {
            maxHeight: appendPx(getResponsiveValue(data, 'maxHeight', isMobile, 'none'))
        }),
        
        // Additional styles override layout styles
        ...additionalStyles
    };
    
    return layoutStyles;
};

// Generate responsive padding shorthand - nur vertikal
export const getResponsivePadding = (data, isMobile = false, fallback = '0') => {
    const top = getResponsiveValue(data, 'paddingTop', isMobile, fallback);
    const bottom = getResponsiveValue(data, 'paddingBottom', isMobile, fallback);
    
    return `${appendPx(top)} 0 ${appendPx(bottom)} 0`;
};

// Generate responsive margin shorthand - nur vertikal
export const getResponsiveMargin = (data, isMobile = false, fallback = '0') => {
    const top = getResponsiveValue(data, 'marginTop', isMobile, fallback);
    const bottom = getResponsiveValue(data, 'marginBottom', isMobile, fallback);
    
    return `${appendPx(top)} 0 ${appendPx(bottom)} 0`;
};

// Check if mobile breakpoint is active
export const isMobileBreakpoint = (containerWidth = 750) => {
    return containerWidth < 750;
};

// Generate CSS custom properties for responsive design
export const generateResponsiveCSSProps = (data, propertyName) => {
    const desktopValue = getResponsiveValue(data, propertyName, false);
    const mobileValue = getResponsiveValue(data, propertyName, true);
    
    return {
        [`--${propertyName}-desktop`]: appendPx(desktopValue),
        [`--${propertyName}-mobile`]: appendPx(mobileValue)
    };
};

// Default layout values for widgets - nur Vertikale
export const DEFAULT_LAYOUT_VALUES = {
    // Padding defaults - nur vertikal
    paddingTop: 40,
    paddingBottom: 40,
    
    // Mobile padding defaults
    paddingTopMobile: 20,
    paddingBottomMobile: 20,
    
    // Margin defaults - nur vertikal
    marginTop: 0,
    marginBottom: 40,
    
    // Mobile margin defaults
    marginTopMobile: 0,
    marginBottomMobile: 20,
    
    // Height dimension defaults - nur Vertikale
    height: 'auto',
    minHeight: 0,
    
    // Mobile height dimension defaults - werden von Layout-Defaults überschrieben
    heightMobile: 1100, // Fallback wenn keine Layout-Defaults
    minHeightMobile: 0
    
    // maxHeight wird nur gesetzt wenn explizit aktiviert
    // Width wird komplett vom System verwaltet (100% Container-Breite)
};

// Merge default values with widget data
export const mergeWithDefaults = (data) => {
    return {
        ...DEFAULT_LAYOUT_VALUES,
        ...data
    };
};
